import { Component, HostBinding, HostListener, inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ThemeConfig } from 'src/app/app-config';
import { Location } from '@angular/common';
import { Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { ProductSpecificationFilterSidebarService } from 'src/app/customer/cf-customer/cf-product/product-specification-filter-sidebar/product-specification-filter-sidebar.service';
import { FilterLayoutConfigService } from 'src/app/Theme/shared/components/filter/filter-layout-config.service';
import { DEFAULT_FILTER_LAYOUT_CONFIG } from 'src/app/Theme/shared/components/filter/filter-layout.model';

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss']
})
export class AdminComponent implements OnInit, OnDestroy {
 // public props
 config: any;
 navCollapsed: any;
 navCollapsedMob: boolean;
 windowWidth: number;

 /** When set, CF spec filter column may pull flush to the collapsed pcoded-navbar (see customer browse SCSS). */
 @HostBinding('class.admin-shell-spec-filter-flush') specFilterFlushToNav = false;

 /**
  * Matches {@link pcoded-navbar} width so fixed overlays (e.g. spec filter) can sit flush to its right edge.
  * Theme: $Menu-collapsed-width 92px / $Menu-width 266px — see src/scss/_variables.scss
  */
 @HostBinding('style.--admin-pcoded-navbar-width')
 get adminPcodedNavbarWidth(): string {
   if (this.config?.layout === 'horizontal') {
     return '0px';
   }
   if (this.windowWidth < 992) {
     return '0px';
   }
   return this.navCollapsed ? '92px' : '266px';
 }

 private readonly destroy$ = new Subject<void>();
 /** Left menu state before the CF specification filter forced collapse (desktop only). */
 private navCollapsedBeforeSpecFilter: boolean | null = null;

 private readonly specFilterSidebar = inject(ProductSpecificationFilterSidebarService);
 private readonly filterLayoutConfig = inject(FilterLayoutConfigService);

 /** Single viewport-docked instance; template mirrors {@link ProductSpecificationFilterSidebarService#isOpen$}. */
 readonly specFilterPanelOpen = toSignal(this.specFilterSidebar.isOpen$, { initialValue: false });

 /** Admin-saved visibility for price range and specification blocks. */
 readonly filterDisplayConfig = toSignal(
   this.filterLayoutConfig.config$.pipe(map((config) => config ?? DEFAULT_FILTER_LAYOUT_CONFIG)),
   { initialValue: DEFAULT_FILTER_LAYOUT_CONFIG },
 );

 /** Layout / child components use this to hide nav chrome while the docked filter column is open. */
 @HostBinding('class.admin-shell-spec-filter-open')
 get adminShellSpecFilterOpen(): boolean {
   return this.specFilterPanelOpen();
 }

 // constructor
 constructor(
   private zone: NgZone,
   private location: Location,
 ) {
   this.config = ThemeConfig;

   let current_url = this.location.path();
   if (this.location['_baseHref']) {
     current_url = this.location['_baseHref'] + this.location.path();
   }

   if (
     current_url === this.location['_baseHref'] + '/layout/collapse-menu' ||
     current_url === this.location['_baseHref'] + '/layout/box'
   ) {
     ThemeConfig.isCollapseMenu = true;
   }

   this.windowWidth = window.innerWidth;
   // Desktop: follow theme. Small screens: start collapsed (icon rail), same as after clicking the sidebar toggle.
   this.navCollapsed = this.windowWidth >= 992 ? ThemeConfig.isCollapseMenu : true;
   this.navCollapsedMob = false;
 }

 ngOnInit(): void {
   this.filterLayoutConfig.ensureLoaded();
   this.specFilterSidebar.isOpen$
     .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
     .subscribe((open) => {
       const syncNav = this.shouldSyncSpecFilterWithNav();
       this.specFilterFlushToNav = open && syncNav;
       if (!syncNav) {
         return;
       }
       if (open) {
         if (this.navCollapsedBeforeSpecFilter === null) {
           this.navCollapsedBeforeSpecFilter = this.navCollapsed;
         }
         this.navCollapsed = true;
       } else if (this.navCollapsedBeforeSpecFilter !== null) {
         this.navCollapsed = this.navCollapsedBeforeSpecFilter;
         this.navCollapsedBeforeSpecFilter = null;
       }
     });
 }

 ngOnDestroy(): void {
   this.destroy$.next();
   this.destroy$.complete();
 }

 @HostListener('window:resize')
 onWindowResize(): void {
   const previousWidth = this.windowWidth;
   this.windowWidth = window.innerWidth;
   const wasDesktop = previousWidth >= 992;
   const isDesktop = this.windowWidth >= 992;
   if (wasDesktop && !isDesktop) {
     this.navCollapsed = true;
     this.navCollapsedMob = false;
     this.closeMenu();
   } else if (!wasDesktop && isDesktop) {
     this.navCollapsed = ThemeConfig.isCollapseMenu;
     this.navCollapsedMob = false;
   }
 }

 /** Spec filter only affects the desktop vertical menu (gives main content + header rail more room). */
 private shouldSyncSpecFilterWithNav(): boolean {
   return this.windowWidth >= 992 && this.config?.layout !== 'horizontal';
 }

 // public method
 navMobClick() {
   if (this.navCollapsedMob && !document.querySelector('app-navigation.pcoded-navbar').classList.contains('mob-open')) {
     this.navCollapsedMob = !this.navCollapsedMob;
     setTimeout(() => {
       this.navCollapsedMob = !this.navCollapsedMob;
     }, 100);
   } else {
     this.navCollapsedMob = !this.navCollapsedMob;
   }
 }
 closeMenu() {
  this.navCollapsedMob = false;
  const nav = document.querySelector('app-navigation.pcoded-navbar');
  if (nav?.classList.contains('mob-open')) {
    nav.classList.remove('mob-open');
  }
}
}
